import type { VirtualPreset } from '../types/Api';

/**
 * Shallow structural comparison of two VirtualPreset objects.
 *
 * Compares `dataQueryId` by value, `gridSettings` keys by strict equality,
 * and `columnsSettings` entries by strict field equality.
 *
 * Replaces the heavy `deep-object-diff` library for this specific use case —
 * VirtualPreset has a known, flat shape that does not require recursive diffing.
 */
export const isVirtualPresetEqual = (a: VirtualPreset, b: VirtualPreset): boolean => {
  if (a === b) {
    return true;
  }
  if (a.dataQueryId !== b.dataQueryId) {
    return false;
  }

  // gridSettings: shallow key comparison (all values are primitives or booleans)
  const aSettings = a.gridSettings;
  const bSettings = b.gridSettings;
  const settingsKeys = Object.keys(aSettings) as (keyof typeof aSettings)[];
  const bSettingsKeys = Object.keys(bSettings);
  if (settingsKeys.length !== bSettingsKeys.length) {
    return false;
  }
  for (const key of settingsKeys) {
    if (aSettings[key] !== bSettings[key]) {
      return false;
    }
  }

  // columnsSettings: length + per-entry field comparison
  const aCols = a.columnsSettings;
  const bCols = b.columnsSettings;
  if (aCols.length !== bCols.length) {
    return false;
  }
  for (let i = 0; i < aCols.length; i++) {
    const ac = aCols[i];
    const bc = bCols[i];
    if (ac.columnKey !== bc.columnKey || ac.visible !== bc.visible || ac.width !== bc.width || ac.pinned !== bc.pinned) {
      return false;
    }
  }
  return true;
};